import React, { useState, useEffect, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon, CheckCircleIcon } from '@heroicons/react/outline';
import { CogIcon } from '@heroicons/react/outline';
import { classNames } from '../../../../helpers/classNames';

/**
 * A new component to display JSON data in a more human-readable format.
 * It renders data in a clean, non-interactive list format.
 */
const FriendlyDisplay = ({ data }) => {
    // Function to render the main content
    const renderContent = (content) => {
        if (content === null || content === undefined) {
            return <span className="text-gray-500 italic">none</span>;
        }

        if (typeof content === 'string') {
            if (content.trim() === '') return <span className="text-gray-500 italic">Empty</span>;
            // Render strings with line breaks preserved
            return <pre className="whitespace-pre-wrap font-sans text-gray-800">{content}</pre>;
        }

        if (typeof content === 'number') {
            return <span className="text-blue-600 font-semibold">{content}</span>;
        }

        if (typeof content === 'boolean') {
            return <span className="text-purple-600 font-semibold">{content ? 'Yes' : 'No'}</span>;
        }

        if (Array.isArray(content)) {
            if (content.length === 0) return <span className="text-gray-500 italic">No items</span>;
            return (
                <ul className="list-disc list-outside space-y-2 pl-5">
                    {content.map((item, index) => (
                        <li key={index}>
                            <FriendlyDisplay data={item} />
                        </li>
                    ))}
                </ul>
            );
        }

        if (typeof content === 'object') {
            const keys = Object.keys(content);
            if (keys.length === 0) return <span className="text-gray-500 italic">No details</span>;

            return (
                <div className="space-y-2 bg-gray-50 p-3 rounded-md mt-2">
                    {keys.map(key => (
                        <div key={key}>
                            <div className="text-sm font-medium text-gray-600 capitalize">
                                {key.replace(/_/g, ' ')}:
                            </div>
                            <div className="mt-1 text-sm text-gray-800">
                                <FriendlyDisplay data={content[key]} />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        return <span className="text-gray-700">{String(content)}</span>;
    };

    // If the data is a string, try to parse it as JSON for better formatting.
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return <FriendlyDisplay data={parsed} />;
        } catch (e) {
            // It's not a JSON string, so render it as is.
        }
    }

    // If the data is an object with a 'content' key (like a tool result), display the content directly.
    if (typeof data === 'object' && data !== null && data.content) {
        return <FriendlyDisplay data={data.content} />;
    }

    return renderContent(data);
};

/**
 * Component to display AI tool usage in a compact collapsible format
 * @param {Object} props 
 * @param {Object} props.toolUse - Tool use data object with name, input, and optional result
 * @param {Function} props.onToggle - Optional callback for when panel is expanded/collapsed
 */
const ToolUseDisplay = ({ toolUse, onToggle }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [allowAutoCollapse, setAllowAutoCollapse] = useState(false);
    const firstOpenTimerRef = useRef(null);
    const scrollDivRef = useRef(null);

    const hasValidInput =
        toolUse?.streaming_input ||
        (toolUse?.input !== null &&
            toolUse?.input !== undefined &&
            (typeof toolUse.input === 'string' ? toolUse.input.trim() : true) &&
            !(typeof toolUse.input === 'object' && Object.keys(toolUse.input).length === 0));

    const hasValidResult =
        toolUse?.result !== null &&
        toolUse?.result !== undefined &&
        !(typeof toolUse.result === 'object' && Object.keys(toolUse.result).length === 0);

    const isToolRunning = hasValidInput && !toolUse?.streaming_input && !hasValidResult;

    // Auto-expand when streaming, auto-collapse when done
    useEffect(() => {
        if (toolUse?.streaming_input) {
            setIsExpanded(true);
        } else if (toolUse?.streaming_input === false && !isToolRunning && allowAutoCollapse) {
            // Only auto-collapse if allowed and not currently running
            setIsExpanded(false);
        }
    }, [toolUse, isToolRunning, allowAutoCollapse]);

    // Auto-expand for 3 seconds when a tool first appears, then allow collapse
    useEffect(() => {
        // When the toolUse id changes (new tool), open for at least 3s
        if (!toolUse) return;
        setIsExpanded(true);
        setAllowAutoCollapse(false);
        if (firstOpenTimerRef.current) {
            try { clearTimeout(firstOpenTimerRef.current); } catch (_) { }
        }
        firstOpenTimerRef.current = setTimeout(() => setAllowAutoCollapse(true), 3000);
        return () => {
            if (firstOpenTimerRef.current) {
                try { clearTimeout(firstOpenTimerRef.current); } catch (_) { }
            }
        };
    }, [toolUse?.id]);

    // Trigger scroll adjustment when expanded state changes
    useEffect(() => {
        if (onToggle && typeof onToggle === 'function') {
            onToggle(isExpanded);
        }

        // Force a scroll adjustment when tool display changes visibility
        if (isExpanded) {
            setTimeout(() => {
                try {
                    const scrollable = document.querySelector('[data-scroll-container]');
                    if (scrollable) {
                        // Check if user is already near bottom
                        const { scrollTop, scrollHeight, clientHeight } = scrollable;
                        const nearBottom = scrollHeight - scrollTop - clientHeight < 200;

                        if (nearBottom) {
                            // User is near bottom, scroll to bottom
                            scrollable.scrollTop = scrollable.scrollHeight;
                        }
                    }
                } catch (error) {
                    console.log('Scroll error in ToolUseDisplay:', error);
                }
            }, 100);
        }
    }, [isExpanded, onToggle]);

    // Scroll to bottom as new content streams in
    useEffect(() => {
        if (isExpanded && scrollDivRef.current) {
            scrollDivRef.current.scrollTop = scrollDivRef.current.scrollHeight;
        }
    }, [isExpanded, toolUse?.input, toolUse?.result]);

    // Don't render if toolUse is invalid or has no meaningful data
    if (!toolUse || !toolUse.name) {
        return null;
    }

    // Don't render if neither input nor result has meaningful data
    if (!hasValidInput && !hasValidResult) {
        return null;
    }

    // Tool configuration with friendly names
    const TOOL_CONFIG = {
        'internet_search': { displayName: 'Web Search' },
        'create_note': { displayName: 'Creating Note' },
        'create_memory': { displayName: 'Storing Memory' },
        'create_email_draft': { displayName: 'Creating Email Draft' },
        'update_email_labels': { displayName: 'Updating Email Labels' },
        'create_email_label': { displayName: 'Creating Email Label' },
        'edit_email_label': { displayName: 'Editing Email Label' },
        'edit_parabot_prompt': { displayName: 'Editing Parabot Prompt' },
        'create_parabot_prompt': { displayName: 'Creating Parabot Prompt' },
        'createReminder': { displayName: 'Creating Reminder' },
        'updateReminder': { displayName: 'Updating Reminder' },
        'update_document': { displayName: 'Updating Document' },
        'move_documents': { displayName: 'Moving Documents' },
        'move_folder': { displayName: 'Moving Folder' },
        'default': { displayName: 'Using Tool' }
    };

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const getDisplayName = () => {
        return toolUse.display_name ||
            TOOL_CONFIG[toolUse.name]?.displayName ||
            toolUse.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()); // Fallback to formatted name
    };

    // Helper function to format input data for display
    const formatInputData = (input, isStreaming = false) => {
        if (input === null || input === undefined) {
            return <span className="text-gray-500 italic">No input data</span>;
        }

        if (isStreaming) {
            const displayStreamingInput = (streamingInput) => {
                const trimmedInput = typeof streamingInput === 'string' ? streamingInput.trim() : streamingInput;

                if (!trimmedInput) {
                    return (
                        <div className="flex items-center bg-gray-50 p-2 rounded-md">
                            <span className="text-gray-500 italic">Preparing input...</span>
                            <div className="ml-2 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        </div>
                    );
                }

                // Try to parse streaming JSON for a friendlier display
                try {
                    const parsed = JSON.parse(trimmedInput);
                    return <FriendlyDisplay data={parsed} />;
                } catch (e) {
                    // If it's not valid JSON yet, show raw text
                    return <pre className="whitespace-pre-wrap font-sans">{trimmedInput}</pre>;
                }
            };

            return (
                <div className="relative">
                    <div className="absolute top-0 right-0 flex items-center text-xs text-blue-600 bg-gray-50 p-2 rounded-md">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-1.5"></div>
                        streaming...
                    </div>
                    {displayStreamingInput(input)}
                </div>
            );
        }

        // For completed input, use the new friendly display.
        return <FriendlyDisplay data={input} />;
    };

    // Helper function to format result data for display
    const formatResultData = (result) => {
        if (result === null || result === undefined) {
            return <span className="text-gray-500 italic">No result data</span>;
        }

        // Use the new friendly display for results.
        return <FriendlyDisplay data={result} />;
    };

    return (
        <div className="mt-2 items-center mb-2">
            <div
                className={classNames(
                    "flex items-center justify-start cursor-pointer"
                )}
                onClick={toggleExpanded}
            >
                <div className="flex items-center">
                    <CogIcon className="h-4 w-4 text-gray-500 mr-2" />
                    <div className={classNames(
                        "text-base text-gray-600 font-bold"
                    )}>
                        {getDisplayName()}
                    </div>
                </div>
                <div className="flex items-center ml-3">
                    {isExpanded ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                    ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                    )}
                </div>
            </div>

            <Transition
                show={isExpanded}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
            >
                <div className="mt-2 pl-2">
                    <div ref={scrollDivRef} className="max-h-96 overflow-y-auto pr-2">
                        <div className="">
                            <div className="space-y-4">
                                <div>
                                    <div className="text-sm font-medium text-gray-700 mb-2">Tool Used:</div>
                                    <div className="text-sm text-gray-600 font-mono bg-white p-2 rounded border">
                                        {toolUse.name}
                                    </div>
                                </div>

                                {hasValidInput && (
                                    <div>
                                        <div className="text-sm font-medium text-gray-700 mb-2">Input:</div>
                                        <div className="text-sm bg-white pl-10 ">
                                            {formatInputData(toolUse.input, toolUse.streaming_input)}
                                        </div>
                                    </div>
                                )}

                                {hasValidResult && (
                                    <div className="mt-4">
                                        <div className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                            Result:
                                        </div>
                                        <div className="text-sm  pl-10   ">
                                            {formatResultData(toolUse.result)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {isToolRunning && (
                        <div className="mt-2 pl-10 pr-2">
                            <div className="flex items-center text-sm text-gray-500">
                                <CogIcon className="h-5 w-5 mr-2 animate-spin" />
                                <span>Running tool...</span>
                            </div>
                        </div>
                    )}
                </div>
            </Transition>
        </div>
    );
};

export default ToolUseDisplay; 